/**
 * Product Carousel Gallery
 * Apple/Insta360-style carousel with clickable thumbnails
 */

if (!customElements.get('product-carousel')) {
  customElements.define(
    'product-carousel',
    class ProductCarousel extends HTMLElement {
      constructor() {
        super();

        this.slides = this.querySelectorAll('.product-carousel__slide');
        this.thumbnails = this.querySelectorAll('.product-carousel__thumb');
        this.dots = this.querySelectorAll('.product-carousel__dot');
        this.prevButton = this.querySelector('.product-carousel__nav--prev');
        this.nextButton = this.querySelector('.product-carousel__nav--next');

        this.currentIndex = 0;
        this.totalSlides = this.slides.length;

        if (this.totalSlides <= 1) return;

        this.init();
      }

      // Pause only media within this carousel, not all page media
      pauseCarouselMedia() {
        this.querySelectorAll('.js-youtube').forEach((video) => {
          video.contentWindow.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*');
        });
        this.querySelectorAll('.js-vimeo').forEach((video) => {
          video.contentWindow.postMessage('{"method":"pause"}', '*');
        });
        this.querySelectorAll('video').forEach((video) => video.pause());
        this.querySelectorAll('product-model').forEach((model) => {
          if (model.modelViewerUI) model.modelViewerUI.pause();
        });
      }

      init() {
        // Thumbnail click handlers - use media ID to find matching slide
        this.thumbnails.forEach((thumb, thumbIndex) => {
          thumb.addEventListener('click', () => {
            const mediaId = thumb.dataset.mediaId;
            this.goToSlideByMediaId(mediaId, thumbIndex);
          });
        });

        // Dot click handlers - use array index
        this.dots.forEach((dot, dotIndex) => {
          dot.addEventListener('click', () => {
            this.goToSlideByThumbIndex(dotIndex);
          });
        });

        // Navigation button handlers
        if (this.prevButton) {
          this.prevButton.addEventListener('click', () => this.prev());
        }

        if (this.nextButton) {
          this.nextButton.addEventListener('click', () => this.next());
        }

        // Keyboard navigation
        this.addEventListener('keydown', (e) => {
          if (e.key === 'ArrowLeft') {
            e.preventDefault();
            this.prev();
          } else if (e.key === 'ArrowRight') {
            e.preventDefault();
            this.next();
          }
        });

        // Touch/swipe support
        this.initSwipe();

        // Update button states
        this.updateNavButtons();
      }

      // Go to slide by thumbnail index (used by thumbnails and dots)
      goToSlideByThumbIndex(thumbIndex) {
        if (thumbIndex < 0 || thumbIndex >= this.thumbnails.length) return;

        const thumb = this.thumbnails[thumbIndex];
        if (thumb) {
          this.goToSlideByMediaId(thumb.dataset.mediaId, thumbIndex);
        }
      }

      // Go to slide by media ID (primary method)
      goToSlideByMediaId(mediaId, thumbIndex = null) {
        // Find the slide with this media ID
        const slideIndex = Array.from(this.slides).findIndex(
          (slide) => slide.dataset.mediaId === mediaId
        );

        if (slideIndex === -1) return;

        // Pause any playing media
        this.pauseCarouselMedia();

        // Update slides
        this.slides.forEach((slide, i) => {
          slide.classList.toggle('is-active', i === slideIndex);
        });

        // Find thumbnail index if not provided
        if (thumbIndex === null) {
          thumbIndex = Array.from(this.thumbnails).findIndex(
            (thumb) => thumb.dataset.mediaId === mediaId
          );
        }

        // Update thumbnails
        this.thumbnails.forEach((thumb, i) => {
          const isActive = i === thumbIndex;
          thumb.classList.toggle('is-active', isActive);
          thumb.setAttribute('aria-current', isActive ? 'true' : 'false');
        });

        // Update dots
        this.dots.forEach((dot, i) => {
          dot.classList.toggle('is-active', i === thumbIndex);
        });

        // Scroll thumbnail into view if needed
        const activeThumb = this.thumbnails[thumbIndex];
        if (activeThumb) {
          const container = this.querySelector('.product-carousel__thumbnails');
          if (container) {
            const thumbRect = activeThumb.getBoundingClientRect();
            const containerRect = container.getBoundingClientRect();

            if (thumbRect.left < containerRect.left || thumbRect.right > containerRect.right) {
              activeThumb.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
            }
          }
        }

        this.currentIndex = thumbIndex >= 0 ? thumbIndex : 0;
        this.updateNavButtons();

        // Dispatch event for other components
        this.dispatchEvent(
          new CustomEvent('slideChanged', {
            detail: {
              currentIndex: this.currentIndex,
              mediaId: mediaId,
            },
          })
        );
      }

      // Legacy method for compatibility
      goToSlide(index) {
        this.goToSlideByThumbIndex(index);
      }

      prev() {
        if (this.currentIndex > 0) {
          this.goToSlideByThumbIndex(this.currentIndex - 1);
        }
      }

      next() {
        if (this.currentIndex < this.thumbnails.length - 1) {
          this.goToSlideByThumbIndex(this.currentIndex + 1);
        }
      }

      updateNavButtons() {
        if (this.prevButton) {
          this.prevButton.disabled = this.currentIndex === 0;
        }

        if (this.nextButton) {
          this.nextButton.disabled = this.currentIndex >= this.thumbnails.length - 1;
        }
      }

      initSwipe() {
        let startX = 0;
        let startY = 0;
        let distX = 0;
        let distY = 0;
        const threshold = 50;
        const restraint = 100;
        const mainArea = this.querySelector('.product-carousel__main');

        if (!mainArea) return;

        mainArea.addEventListener(
          'touchstart',
          (e) => {
            const touch = e.changedTouches[0];
            startX = touch.pageX;
            startY = touch.pageY;
          },
          { passive: true }
        );

        mainArea.addEventListener(
          'touchend',
          (e) => {
            const touch = e.changedTouches[0];
            distX = touch.pageX - startX;
            distY = touch.pageY - startY;

            if (Math.abs(distX) >= threshold && Math.abs(distY) <= restraint) {
              if (distX > 0) {
                this.prev();
              } else {
                this.next();
              }
            }
          },
          { passive: true }
        );
      }

      // Reinitialize after DOM content is replaced
      reinit() {
        this.slides = this.querySelectorAll('.product-carousel__slide');
        this.thumbnails = this.querySelectorAll('.product-carousel__thumb');
        this.dots = this.querySelectorAll('.product-carousel__dot');
        this.totalSlides = this.slides.length;
        this.currentIndex = 0;

        // Re-attach thumbnail click handlers - use media ID
        this.thumbnails.forEach((thumb, thumbIndex) => {
          thumb.addEventListener('click', () => {
            const mediaId = thumb.dataset.mediaId;
            this.goToSlideByMediaId(mediaId, thumbIndex);
          });
        });

        // Re-attach dot click handlers - use array index
        this.dots.forEach((dot, dotIndex) => {
          dot.addEventListener('click', () => {
            this.goToSlideByThumbIndex(dotIndex);
          });
        });

        this.updateNavButtons();
      }

      // Method to be called when variant changes
      setActiveMedia(mediaId, prepend = false) {
        // Pause any playing media
        this.pauseCarouselMedia();

        // Find the slide with this media ID
        const targetSlide = Array.from(this.slides).find(
          (slide) => slide.dataset.mediaId === mediaId
        );

        if (!targetSlide) return;

        // Remove is-active from all slides and add to target
        this.slides.forEach((slide) => slide.classList.remove('is-active'));
        targetSlide.classList.add('is-active');

        // Always select the first thumbnail when variant changes
        // (variant images don't have thumbnails, so we show the first one)
        this.thumbnails.forEach((thumb, i) => {
          const isActive = i === 0;
          thumb.classList.toggle('is-active', isActive);
          thumb.setAttribute('aria-current', isActive ? 'true' : 'false');
        });

        // Update dots - select first one
        this.dots.forEach((dot, i) => {
          dot.classList.toggle('is-active', i === 0);
        });

        // Scroll first thumbnail into view
        const firstThumb = this.thumbnails[0];
        if (firstThumb) {
          const container = this.querySelector('.product-carousel__thumbnails');
          if (container) {
            container.scrollTo({ left: 0, behavior: 'smooth' });
          }
        }

        // Update internal state
        this.currentIndex = 0;
        this.updateNavButtons();
      }
    }
  );
}
